'use client';

import { c as _c } from "react/compiler-runtime";
import type { KeyboardEvent, MouseEvent } from 'react';
import { useEffect, useEffectEvent, useLayoutEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { toast } from 'sonner';
import { DESCRIPTION_COLLAPSED_HEIGHT_PX, PrV2DescriptionSkeleton } from '../shared/skeletons/pr-v2-loading-sections';
import { MarkdownRenderer } from '@/components/markdown-renderer/markdown-renderer';
import { AutosizeTextarea } from '@/components/textarea/autosize-textarea';
import { TEXTAREA_VARIANTS } from '@/components/textarea/types';
import { usePrV2UpdateDescription } from '@/data/v2/pr/use-pr-v2-update-description';
import { withoutDemoVideoBlock } from '@/lib/code-sessions/pull-request-demo/demo-block';
import { cn } from '@/lib/utils';
import { stripGeneratedPrDescriptionFooter } from '@/utils/pr-description-enhancement/generated-footer';
type PrV2DescriptionProps = {
  owner: string;
  repo: string;
  number: string;
  body: string | null;
  readOnly?: boolean;
  isAwaitingFullData?: boolean;
  /** The page shows the demo in its own card, so the block cubic wrote for GitHub stays out. */
  hideDemoVideoBlock?: boolean;
};

// Long descriptions collapse so the Changes/Discussion tabs stay above the fold.
// Collapse only when the content meaningfully overflows the cap — fading out a
// few hidden pixels reads as a rendering glitch, not truncation.
const DESCRIPTION_COLLAPSE_THRESHOLD_PX = 320;
const descriptionMarkdownClassName = cn('max-w-none', '[&_h2:has(+details>summary:empty)]:pl-4', '[&_h2+details:has(>summary:empty)]:relative', '[&_h2+details:has(>summary:empty)]:-mt-[1.8rem]', '[&_h2+details:has(>summary:empty)]:mb-10', '[&_h2+details[open]:has(>summary:empty)]:pt-7', '[&_h2+details>summary:empty]:absolute', '[&_h2+details>summary:empty]:left-0', '[&_h2+details>summary:empty]:top-0', '[&_h2+details>summary:empty]:h-6', '[&_h2+details>summary:empty]:w-4', '[&_h2+details>summary:empty]:leading-6');
function isInteractiveDescriptionTarget(target: EventTarget | null, currentContainer?: Element | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  const nearestButtonLikeElement = target.closest('[role="button"]');
  const isLink = target.tagName === 'A' || target.closest('a');
  const isImage = target.tagName === 'IMG' || target.closest('img');
  const isVideo = target.tagName === 'VIDEO' || target.closest('video');
  const isDetails = target.tagName === 'DETAILS' || target.closest('details');
  const isPreviewElement = nearestButtonLikeElement && nearestButtonLikeElement !== currentContainer || target.closest('[data-state="open"]') || target.getAttribute('target') === '_blank' || target.closest('[target="_blank"]');
  return Boolean(isLink || isImage || isVideo || isDetails || isPreviewElement);
}
export function PrV2Description(t0) {
  const $ = _c(58);
  const {
    owner,
    repo,
    number,
    body,
    readOnly: t1,
    isAwaitingFullData,
    hideDemoVideoBlock: t2
  } = t0;
  const readOnly = t1 === undefined ? false : t1;
  const hideDemoVideoBlock = t2 === undefined ? false : t2;
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [canCollapse, setCanCollapse] = useState(false);
  const [draftBody, setDraftBody] = useState(body ?? "");
  const [optimisticBody, setOptimisticBody] = useState(null);
  const descriptionMutation = usePrV2UpdateDescription(owner, repo, number);
  const editingContainerRef = useRef(null);
  const displayContentRef = useRef(null);
  const mutationCounterRef = useRef(0);
  if (optimisticBody !== null && body === optimisticBody && !descriptionMutation.isPending) {
    setOptimisticBody(null);
  }
  const displayedBody = optimisticBody ?? body ?? "";
  const bodyWithoutDemoBlock = hideDemoVideoBlock ? withoutDemoVideoBlock(displayedBody) : displayedBody;
  const renderedBody = stripGeneratedPrDescriptionFooter(bodyWithoutDemoBlock);
  const hasHiddenDemoBlock = bodyWithoutDemoBlock !== displayedBody;
  const isCollapsed = canCollapse && !isExpanded && !isEditing;
  let t3;
  if ($[0] !== isEditing) {
    t3 = () => {
      if (isEditing) {
        return;
      }
      const content = displayContentRef.current;
      if (!content) {
        return;
      }
      const measure = () => {
        setCanCollapse(content.scrollHeight > DESCRIPTION_COLLAPSE_THRESHOLD_PX);
      };
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(content);
      return () => observer.disconnect();
    };
    $[0] = isEditing;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== body || $[3] !== isAwaitingFullData || $[4] !== isEditing) {
    t4 = [isEditing, body, isAwaitingFullData];
    $[2] = body;
    $[3] = isAwaitingFullData;
    $[4] = isEditing;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useLayoutEffect(t3, t4);
  let t5;
  if ($[6] !== displayedBody) {
    t5 = () => {
      const scrollContainer = document.getElementById("scrollable-content");
      const scrollTop = scrollContainer?.scrollTop ?? 0;
      setDraftBody(displayedBody);
      setIsEditing(true);
      requestAnimationFrame(() => {
        if (scrollContainer) {
          scrollContainer.scrollTop = scrollTop;
        }
      });
    };
    $[6] = displayedBody;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const activateEditing = t5;
  let t6;
  if ($[8] !== activateEditing || $[9] !== isCollapsed || $[10] !== readOnly) {
    t6 = event => {
      const target = event.target instanceof Element ? event.target : null;
      const summary = target?.closest("summary");
      if (summary && event.currentTarget.contains(summary) && !target?.closest("a")) {
        const details = summary.closest("details");
        if (details && !details.open) {
          const detailsElements = displayContentRef.current?.querySelectorAll("details");
          const detailsIndex = detailsElements ? Array.from(detailsElements).indexOf(details) : -1;
          event.preventDefault();
          setIsExpanded(true);
          requestAnimationFrame(() => {
            const renderedDetails = detailsIndex >= 0 ? displayContentRef.current?.querySelectorAll("details")[detailsIndex] : details;
            if (renderedDetails) {
              renderedDetails.open = true;
            }
          });
        }
        return;
      }
      if (isInteractiveDescriptionTarget(event.target, event.currentTarget)) {
        return;
      }
      if (isCollapsed) {
        setIsExpanded(true);
        return;
      }
      if (readOnly) {
        return;
      }
      activateEditing();
    };
    $[8] = activateEditing;
    $[9] = isCollapsed;
    $[10] = readOnly;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleDisplayClick = t6;
  let t7;
  if ($[12] !== activateEditing || $[13] !== isCollapsed || $[14] !== readOnly) {
    t7 = event_0 => {
      if (event_0.key !== "Enter" && event_0.key !== " ") {
        return;
      }
      if (isInteractiveDescriptionTarget(event_0.target, event_0.currentTarget)) {
        return;
      }
      event_0.preventDefault();
      if (isCollapsed) {
        setIsExpanded(true);
        return;
      }
      if (readOnly) {
        return;
      }
      activateEditing();
    };
    $[12] = activateEditing;
    $[13] = isCollapsed;
    $[14] = readOnly;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleDisplayKeyDown = t7;
  let t8;
  if ($[16] !== displayedBody) {
    t8 = () => {
      setDraftBody(displayedBody);
      setIsEditing(false);
    };
    $[16] = displayedBody;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const cancelEditing = t8;
  let t9;
  if ($[18] !== cancelEditing) {
    t9 = event_1 => {
      event_1.preventDefault();
      cancelEditing();
    };
    $[18] = cancelEditing;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== isEditing) {
    t10 = {
      enableOnFormTags: true,
      enabled: isEditing
    };
    $[20] = isEditing;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== displayedBody || $[23] !== isEditing) {
    t11 = [isEditing, displayedBody];
    $[22] = displayedBody;
    $[23] = isEditing;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  useHotkeys("esc", t9, t10, t11);
  let t12;
  if ($[25] !== descriptionMutation || $[26] !== displayedBody || $[27] !== draftBody) {
    t12 = t13 => {
      const nextBody = t13 === undefined ? draftBody : t13;
      const currentBody = displayedBody;
      const normalizedNextBody = nextBody.trim();
      const normalizedCurrentBody = currentBody.trim();
      if (normalizedNextBody === normalizedCurrentBody) {
        setIsEditing(false);
        return;
      }
      const myMutationId = mutationCounterRef.current = mutationCounterRef.current + 1;
      setOptimisticBody(normalizedNextBody);
      setDraftBody(normalizedNextBody);
      setIsEditing(false);
      descriptionMutation.mutate(normalizedNextBody, {
        onSuccess: t14 => {
          const {
            body: savedBody
          } = t14;
          if (mutationCounterRef.current !== myMutationId) {
            return;
          }
          setOptimisticBody(savedBody);
          toast.success("Description updated");
        },
        onError: (error, submittedBody) => {
          if (mutationCounterRef.current !== myMutationId) {
            return;
          }
          setOptimisticBody(null);
          setDraftBody(submittedBody);
          setIsEditing(true);
          toast.error(error instanceof Error ? error.message : "Failed to update description");
        }
      });
    };
    $[25] = descriptionMutation;
    $[26] = displayedBody;
    $[27] = draftBody;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const saveDescription = t12;
  let t13;
  if ($[29] !== saveDescription) {
    t13 = event_2 => {
      if (event_2.target instanceof Element && event_2.target.closest("[data-radix-popper-content-wrapper]")) {
        return;
      }
      if (editingContainerRef.current && !editingContainerRef.current.contains(event_2.target as Node)) {
        saveDescription();
      }
    };
    $[29] = saveDescription;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const handleClickOutside = useEffectEvent(t13);
  let t14;
  if ($[31] !== handleClickOutside || $[32] !== isEditing) {
    t14 = () => {
      if (!isEditing) {
        return;
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    };
    $[31] = handleClickOutside;
    $[32] = isEditing;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== isEditing) {
    t15 = [isEditing];
    $[34] = isEditing;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[36] !== number || $[37] !== owner || $[38] !== repo) {
    t16 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const navStart = (window as any).__PR_NAV_START;
      if (!navStart) {
        return;
      }
      const elapsed = performance.now() - navStart;
      console.warn(`[PREFETCH] click-to-description ${owner}/${repo}#${number} ${elapsed.toFixed(0)}ms`);
      (window as any).__PR_NAV_START = undefined;
    };
    t17 = [owner, repo, number];
    $[36] = number;
    $[37] = owner;
    $[38] = repo;
    $[39] = t16;
    $[40] = t17;
  } else {
    t16 = $[39];
    t17 = $[40];
  }
  useEffect(t16, t17);
  const hasDescription = Boolean(renderedBody.trim().length > 0);
  if (body === null && isAwaitingFullData) {
    let t18;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <PrV2DescriptionSkeleton />;
      $[41] = t18;
    } else {
      t18 = $[41];
    }
    return t18;
  }
  let t18;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = cn("group prose prose-invert -ml-2 w-[calc(100%+1rem)]", "max-w-[840px]");
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== canCollapse || $[44] !== cancelEditing || $[45] !== draftBody || $[46] !== handleDisplayClick || $[47] !== handleDisplayKeyDown || $[48] !== hasDescription || $[49] !== hasHiddenDemoBlock || $[50] !== isCollapsed || $[51] !== isEditing || $[52] !== owner || $[53] !== readOnly || $[54] !== renderedBody || $[55] !== repo || $[56] !== saveDescription) {
    t19 = <div className={t18}>{isEditing ? <div ref={editingContainerRef} className="px-3 py-2"><AutosizeTextarea owner={owner} repo={repo} data-hotkeys-ignore="true" variant={TEXTAREA_VARIANTS.editingSavedContent} showSendButton={false} minHeight={24} name="description" enableUserMentions={true} className="w-full resize-none border-none bg-transparent p-0 py-2 focus:outline-hidden md:text-base" placeholder="Add description..." value={draftBody} onChange={event_3 => setDraftBody(event_3.target.value)} handleSubmit={text => {
          saveDescription(text);
        }} handleCancel={cancelEditing} aria-label="Pull request description" /></div> : <div data-testid="pr-v2-description-display" className={cn("min-h-6 rounded-md px-3 py-1 transition-colors md:py-2", readOnly && !isCollapsed ? "cursor-default" : "cursor-pointer hover:bg-zinc-900", "[&_img]:cursor-default [&_video]:cursor-default", isCollapsed && "relative overflow-hidden")} style={isCollapsed ? {
        maxHeight: DESCRIPTION_COLLAPSED_HEIGHT_PX
      } : undefined} role={readOnly && !isCollapsed ? undefined : "button"} tabIndex={readOnly && !isCollapsed ? undefined : 0} aria-label={isCollapsed ? "Expand pull request description" : readOnly ? undefined : "Edit pull request description"} aria-expanded={canCollapse ? !isCollapsed : undefined} onClick={handleDisplayClick} onKeyDown={handleDisplayKeyDown}><div ref={displayContentRef}>{hasDescription ? <MarkdownRenderer content={renderedBody} org={owner} repo={repo} className={descriptionMarkdownClassName} baseTextSize="sm" density="compact" linkifyPullRequestReferences={true} linkifyCommitReferences={true} /> : !hasHiddenDemoBlock ? <p className="text-sm text-zinc-500">No description provided on this pull request.</p> : null}</div>{isCollapsed ? <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-24 rounded-b-md bg-gradient-to-t from-background to-transparent group-hover:from-zinc-900" /> : null}</div>}</div>;
    $[43] = canCollapse;
    $[44] = cancelEditing;
    $[45] = draftBody;
    $[46] = handleDisplayClick;
    $[47] = handleDisplayKeyDown;
    $[48] = hasDescription;
    $[49] = hasHiddenDemoBlock;
    $[50] = isCollapsed;
    $[51] = isEditing;
    $[52] = owner;
    $[53] = readOnly;
    $[54] = renderedBody;
    $[55] = repo;
    $[56] = saveDescription;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  return t19;
}